import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, MessageCircle, Phone } from "lucide-react";
import { SERVICES, COMPANY, waLink } from "@/lib/site";
import { Reveal } from "@/components/Reveal";

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = SERVICES.find((x) => x.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!s) return <Navigate to="/" replace />;

  const others = SERVICES.filter((x) => x.slug !== slug).slice(0, 3);

  return (
    <main data-testid="service-detail-page" className="w-full bg-[#0a0a0a]">
      <section className="relative w-full min-h-[62svh] flex items-end overflow-hidden grain">
        <motion.img
          src={s.img}
          alt={s.title}
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
        <div className="relative z-10 w-full px-5 sm:px-8 lg:px-12 pt-32 pb-12">
          <Link
            to="/"
            data-testid="service-back-link"
            className="inline-flex items-center gap-2 font-mono-t text-[11px] uppercase tracking-widest text-neutral-300 hover:text-[#f95700] transition-colors"
          >
            <ArrowLeft size={14} /> Alle Leistungen
          </Link>
          <div className="mt-6 flex items-center gap-3">
            <span className="font-mono-t text-[10px] uppercase tracking-widest bg-[#f95700] text-white px-3 py-1.5">
              {s.tag}
            </span>
          </div>
          <h1
            data-testid="service-detail-title"
            className="mt-5 font-display uppercase font-black leading-[0.9] tracking-tight text-white text-[clamp(2.3rem,8.5vw,6rem)]"
          >
            {s.title}
          </h1>
        </div>
      </section>

      <section className="w-full px-5 sm:px-8 lg:px-12 py-16 sm:py-20 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="text-lg sm:text-xl text-white leading-relaxed font-semibold">{s.text}</p>
          </Reveal>
          <Reveal delay={0.06}>
            <p className="mt-6 text-[15px] sm:text-base text-neutral-400 leading-relaxed">{s.long}</p>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 gap-px bg-white/10">
            {s.bullets.map((b, i) => (
              <Reveal key={b} delay={0.05 * i} className="bg-[#141414] p-5 flex items-start gap-3">
                <span className="w-6 h-6 shrink-0 grid place-items-center bg-[#f95700]">
                  <Check size={14} className="text-white" strokeWidth={3} />
                </span>
                <span className="text-sm text-neutral-200 leading-relaxed">{b}</span>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <Reveal className="border border-white/12 bg-[#121212] p-6 sm:p-8 lg:sticky lg:top-28">
            <div className="eyebrow text-[#f95700] mb-3">Angebot anfordern</div>
            <h2 className="font-display text-2xl sm:text-3xl font-black uppercase text-white leading-tight">
              {s.title} anfragen
            </h2>
            <p className="mt-4 text-sm text-neutral-400 leading-relaxed">
              Schildern Sie uns kurz Ihr Vorhaben – am schnellsten per WhatsApp, gern mit Fotos.
              Sie erhalten anschließend ein individuelles Angebot.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <a
                href={waLink(`Hallo Global Move Europa, ich interessiere mich für: ${s.title}. Bitte senden Sie mir ein Angebot.`)}
                target="_blank"
                rel="noreferrer"
                data-testid="service-detail-whatsapp"
                className="inline-flex items-center justify-between gap-4 bg-[#25D366] hover:bg-[#1eb857] text-white h-15 py-4 px-6 font-display text-base font-bold uppercase tracking-wide transition-colors"
              >
                Per WhatsApp anfragen
                <MessageCircle size={20} />
              </a>
              <a
                href="/#konfigurator"
                data-testid="service-detail-configurator"
                className="inline-flex items-center justify-between gap-4 bg-[#f95700] hover:bg-[#e04e00] text-white py-4 px-6 font-display text-base font-bold uppercase tracking-wide transition-colors"
              >
                Konfigurator ausfüllen
                <ArrowRight size={20} />
              </a>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                data-testid="service-detail-call"
                className="inline-flex items-center justify-between gap-4 border border-white/25 hover:border-white text-white py-4 px-6 font-display text-base font-bold uppercase tracking-wide transition-colors"
              >
                {COMPANY.phoneDisplay}
                <Phone size={18} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="w-full px-5 sm:px-8 lg:px-12 pb-20 sm:pb-28">
        <div className="eyebrow text-neutral-500 mb-6">Weitere Leistungen</div>
        <div className="grid sm:grid-cols-3 gap-px bg-white/10">
          {others.map((o) => (
            <Link
              key={o.slug}
              to={`/leistungen/${o.slug}`}
              data-testid={`service-related-${o.slug}`}
              className="group bg-[#141414] p-6 flex items-center justify-between gap-4 hover:bg-[#1b1b1b] transition-colors"
            >
              <span className="font-display text-lg sm:text-xl font-bold uppercase text-white group-hover:text-[#f95700] transition-colors">
                {o.title}
              </span>
              <ArrowRight size={18} className="text-[#f95700] transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
